import { useCallback, useEffect, useRef } from 'react';

import useRouter from 'js/lib/useRouter';

const SHOW_SKILL_PROGRESS_PARAM = 'showSkillProgressForPathway';

type UseSkillProgressDeepLinkParams = {
  /** Whether this is a skill pathway (only skill pathways support this deep link) */
  isSkillPathway: boolean | undefined;
  /** The badgeTemplateId for this skill pathway */
  badgeTemplateId: string | undefined;
  /** Callback to execute when the deep link matches this badge */
  onDeepLinkMatch: () => void;
};

type UseSkillProgressDeepLinkResult = {
  /** Call this function to clear the deep link param from the URL (typically on dialog close) */
  clearDeepLinkParam: () => void;
};

/**
 * Hook for handling deep links to the Verified Skills Progress dialog.
 *
 * Checks if the `showSkillProgressForPathway` query param matches the provided badgeTemplateId,
 * and if so, executes the callback. The param is kept in the URL until `clearDeepLinkParam` is called,
 * allowing proper back button behavior (back button will re-open the dialog).
 *
 * @example
 * const { clearDeepLinkParam } = useSkillProgressDeepLink({
 *   isSkillPathway: badgeMetadata?.isSkillPathway,
 *   badgeTemplateId: badgeMetadata?.badge?.badgeTemplateId,
 *   onDeepLinkMatch: () => setSkillsDialogOpen(true),
 * });
 *
 * // Call clearDeepLinkParam when the dialog closes
 * <SkillsDialog onClose={() => { clearDeepLinkParam(); setSkillsDialogOpen(false); }} />
 */
export const useSkillProgressDeepLink = ({
  isSkillPathway,
  badgeTemplateId,
  onDeepLinkMatch,
}: UseSkillProgressDeepLinkParams): UseSkillProgressDeepLinkResult => {
  const { push, location } = useRouter();
  const hasProcessedDeepLink = useRef(false);

  const showSkillProgressForPathwayParam = location.query?.[SHOW_SKILL_PROGRESS_PARAM];
  const isDeepLinkActive =
    isSkillPathway &&
    showSkillProgressForPathwayParam &&
    badgeTemplateId &&
    showSkillProgressForPathwayParam === badgeTemplateId;

  useEffect(() => {
    // Only trigger the callback once per deep link session
    if (isDeepLinkActive && !hasProcessedDeepLink.current) {
      hasProcessedDeepLink.current = true;
      onDeepLinkMatch();
    }
  }, [isDeepLinkActive, onDeepLinkMatch]);

  // Reset the ref when the deep link param is cleared (allows back button to re-trigger)
  useEffect(() => {
    if (!showSkillProgressForPathwayParam) {
      hasProcessedDeepLink.current = false;
    }
  }, [showSkillProgressForPathwayParam]);

  const clearDeepLinkParam = useCallback<() => void>(() => {
    if (location.query?.[SHOW_SKILL_PROGRESS_PARAM]) {
      const { [SHOW_SKILL_PROGRESS_PARAM]: _, ...remainingQuery } = location.query || {};

      push({
        pathname: location.pathname,
        query: remainingQuery,
      });
    }
  }, [location.query, location.pathname, push]);

  return { clearDeepLinkParam };
};
